import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CmsPublicApiClient, {
  CmsApiError,
} from "../../../services/api/cmsPublicApiClient";
import { mapNoticiaDetailToVM } from "../../../services/mappers/cmsPublicMappers";
import { NoticiaDetailVM } from "../../../types/viewModels/cmsPublic";

const service = new CmsPublicApiClient();

export function useNewsDetail() {
  const { t } = useTranslation("news");
  const { id } = useParams();
  const slug = useMemo(() => decodeURIComponent(id || ""), [id]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [noticia, setNoticia] = useState<NoticiaDetailVM | null>(null);

  const fetchNoticia = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      setError(t("errors.notFound"));
      return;
    }

    setLoading(true);
    setError("");
    setNotFound(false);

    try {
      const response = await service.getNoticiaBySlug(slug);
      setNoticia(mapNoticiaDetailToVM(response.data));
    } catch (requestError) {
      setNoticia(null);

      if (requestError instanceof CmsApiError) {
        if (requestError.status === 404) {
          setNotFound(true);
          setError(t("errors.notFound"));
          return;
        }

        if (requestError.status === 400 || requestError.status === 422) {
          setError(t("errors.invalidQuery"));
          return;
        }

        setError(t("errors.generic"));
        return;
      }

      setError(t("errors.unexpected"));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchNoticia();
  }, [fetchNoticia]);

  return {
    loading,
    error,
    notFound,
    noticia,
    retry: fetchNoticia,
  };
}

export default useNewsDetail;
